import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { LiveLesson } from 'src/app/types/live-lesson-types';
import { LiveLessonCountdownTimerComponent } from './live-lesson-countdown-timer/live-lesson-countdown-timer.component';

// Lessons are scheduled in UK time — comparing calendar days via the viewer's own local
// getFullYear/getMonth/getDate would misjudge "is this today" near midnight UK time for anyone
// outside it (e.g. an 11:30PM UK lesson could read as "tomorrow" for a viewer further east).
// en-CA conveniently formats as YYYY-MM-DD, so this becomes a plain string comparison once both
// dates are expressed in the UK's own calendar day.
function isSameUkDay(a: Date, b: Date): boolean {
  const toUkDateKey = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
  return toUkDateKey(a) === toUkDateKey(b);
}

/** Side-nav card for upcoming (not live-yet) lessons in the next 5 days — the parent
 * (SideNavMenuComponent) owns the 5-day filtering/sorting, this just branches on how many
 * there are. Replaces the old `app-live-lesson-upcoming` (today-only, no countdown). The
 * separate "live right now" banner (`app-live-lesson-notification`) is a different state
 * entirely and isn't affected by this component. */
@Component({
  selector: 'app-live-lesson-countdown',
  standalone: true,
  imports: [CommonModule, RouterLink, LiveLessonCountdownTimerComponent],
  templateUrl: './live-lesson-countdown.component.html',
  styleUrl: './live-lesson-countdown.component.scss',
})
export class LiveLessonCountdownComponent {
  lessons = input.required<LiveLesson[]>();
  openSchedule = output<void>();

  isSingle = computed(() => this.lessons().length === 1);
  
  displayLessons = computed(() => this.lessons().slice(0, 2));

  allToday = computed(() => {
    const now = new Date();
    return this.lessons().every((l) => isSameUkDay(new Date(l.starts_at), now));
  });

  heading = computed(() => {
    const n = this.displayLessons().length;
    return `${n} Live Lesson${n > 1 ? 's' : ''} upcoming`;
  });

  // "both" only reads correctly for exactly 2 — 3+ needs "all" instead.
  scheduleButtonLabel = computed(() =>
    this.lessons().length > 2 ? 'View all live lessons' : 'View both live lessons',
  );

  // Explicit Europe/London everywhere below — without it, these default to the *viewer's own*
  // browser timezone, which would silently show e.g. a US-based viewer their own local
  // equivalent time while the UI still labels it "(UK)" in the template.
  formattedDate(lesson: LiveLesson): string {
    return new Date(lesson.starts_at).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'Europe/London',
    });
  }

  formattedTime(lesson: LiveLesson): string {
    return new Date(lesson.starts_at).toLocaleTimeString('en-GB', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Europe/London',
    });
  }
}
